import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useFormValidation } from '../../../utils/useFormValidation';
import { useTranslation } from '../../../utils/translations';
import {
  useLogEvent,
  useAutoIntelTracking,
} from '../../../utils/useAnalytics';
import {
  useGetConfiguration,
  useGetActiveBackgroundIDs,
  useAccessoryStore,
  usePackageStore,
} from '@adloid-technologies/discover-sdk';
import { AnalyticsIds } from '../../../utils/analytics';
import { linkId, uimetadata } from '../../../utils/metadata';
import {
  updateGMDigitalDataSiteSection,
  DataDtm,
} from '../../../utils/externalAnalyticsGM';
import {
  getApiMode,
  getCountryParameter,
  getDealerNameParameter,
  getShowroom,
} from '../../../utils/common';
import { getConfiguredVehicleCode } from '../../../utils/configuredVehicleCode';
import { getBrandItemCode } from '../../../utils/configuredVehicleCode';
import { SaveStep, SaveOption } from '../SaveDialog.types';

// Only the book-a-test-drive form collects an appointment slot (1.0 gates the
// same two inputs behind `formDetails.id === 'book-test-drive'`).
const TEST_DRIVE_OPTION: SaveOption = 'test-drive';

// Al Ghandi - Mirdiff City Center: the pilot dealer 1.0 pins to the top of every
// dealership dropdown it appears in (`dealersMirdifFirst` in utils/country.ts).
const MIRDIF_DEALER_ID = '334821';

const ECOMM_SVC = {
  main: 'https://autodome-api-ecomm.metadome.ai',
  staging: 'https://autodome-api-ecomm-staging.metadome.ai',
  dev: 'https://autodome-api-ecomm-dev.metadome.ai',
};

// Converts the native date input's YYYY-MM-DD to the DD/MM/YYYY the WhatsApp
// template expects. Returns '' for an unparseable value rather than emitting a
// malformed date into a customer-facing message.
const formatDateForLead = (value: string) => {
  const parts = (value || '').split('-');
  if (parts.length !== 3) return '';
  const [year, month, day] = parts;
  return `${day}/${month}/${year}`;
};

export const useSaveDialog = (
  isOpen: boolean,
  onClose: () => void,
  source: string = ''
) => {
  const { t, currentLanguage } = useTranslation();
  const { trackEvent } = useLogEvent();
  const { trackAutoIntelOnly } = useAutoIntelTracking();
  // The *selected* colours, not the ones merely available for the variant.
  // `useGetActiveColorIDs` returns every colour whose relevantVariants matches
  // the trim — the array ConfigPanel maps over to draw the swatch grid — so
  // reading `[0]` off it always reported the first swatch (Black on most GM
  // trims) no matter what the user picked.
  const { variantID, view, featureId, exteriorColorID, interiorColorID } =
    useGetConfiguration();
  const { backgroundIDs } = useGetActiveBackgroundIDs();
  // Selected (not merely available) accessories/packages — these feed both the
  // configured-vehicle code and state_info.
  const selectedAccessoryIDs = useAccessoryStore((s) => s.accessoriesIDs);
  const selectedPackageIDs = usePackageStore((s) => s.packageIDs);
  const [searchParams] = useState(() => new URLSearchParams(window.location.search));
  const country = getCountryParameter();

  // Per-country dial code (prefixed onto mobile_number) and Salesforce lead
  // country code (metadome_country_code). Mirrors 1.0's config.countryCodes.
  const { dialCode, leadCountryCode } = useMemo(() => {
    const codes = uimetadata.config?.countryCodes;
    const entry = codes?.[country] ?? codes?.default;
    if (typeof entry === 'string') return { dialCode: entry, leadCountryCode: entry };
    return {
      dialCode: entry?.code || '+971',
      leadCountryCode: entry?.leadCode || '',
    };
  }, [country]);

  const [step, setStep] = useState<SaveStep>('options');
  const [selectedOption, setSelectedOption] = useState<SaveOption | null>(null);
  const [selectedPrefix, setSelectedPrefix] = useState(dialCode);
  const [isMarketingAccepted, setIsMarketingAccepted] = useState(true);
  const [backendError, setBackendError] = useState<string | null>(null);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const qrCodeUrlRef = useRef('');

  const [formData, setFormData] = useState<Record<string, string>>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    model: uimetadata.config?.modelName || '',
    dealer: '',
    testDriveDate: '',
    testDriveTime: '',
    comments: '',
  });

  const [loading, setLoading] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [isFailed, setIsFailed] = useState(false);

  const setFieldValue = useCallback((field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  // Release the blob URL when it is replaced or the hook unmounts.
  const setQrFromBlob = useCallback((blob: Blob) => {
    if (qrCodeUrlRef.current) URL.revokeObjectURL(qrCodeUrlRef.current);
    const url = URL.createObjectURL(blob);
    qrCodeUrlRef.current = url;
    setQrCodeUrl(url);
  }, []);

  useEffect(
    () => () => {
      if (qrCodeUrlRef.current) URL.revokeObjectURL(qrCodeUrlRef.current);
    },
    []
  );

  const downloadQRCode = useCallback(() => {
    if (!qrCodeUrl) return;
    const link = document.createElement('a');
    link.href = qrCodeUrl;
    link.download = 'configuration-qr.png';
    document.body.appendChild(link);
    link.click();
    link.remove();
  }, [qrCodeUrl]);

  const openQRCodeInNewTab = useCallback(() => {
    if (!qrCodeUrl) return;
    window.open(qrCodeUrl, '_blank');
  }, [qrCodeUrl]);

  // Get dealers from ui-metadata based on country
  const dealers = useMemo(() => {
    const dealersData = (uimetadata as any).dealers;
    const countryList = dealersData?.[country] || dealersData?.default || [];

    // 1.0's filter: country first, then ?dealerName= as a sub-filter. A name
    // that matches nothing falls back to the whole country list.
    const dealerNameParam = getDealerNameParameter();
    const byName = dealerNameParam
      ? countryList.filter((d: any) => d.dealerName === dealerNameParam)
      : [];
    const raw = byName.length > 0 ? byName : countryList;

    // 1.0's `dealersMirdifFirst`: the Al Ghandi - Mirdiff City Center pilot
    // dealer leads the dropdown wherever it appears. Lists without it are
    // returned unchanged.
    const mirdif = raw.findIndex((d: any) => d.dealerId === MIRDIF_DEALER_ID);
    const countryDealers =
      mirdif > 0
        ? [raw[mirdif], ...raw.slice(0, mirdif), ...raw.slice(mirdif + 1)]
        : raw;

    // Return each dealer location as an individual entry
    return countryDealers.map((dealer: any) => ({
      id: dealer.dealerId,
      name:
        dealer.label?.[currentLanguage] ||
        dealer.label?.en ||
        dealer.dealerName,
      dealerName: dealer.dealerName,
      locationCount: 1,
      country: dealer.country,
      address: dealer.label?.[currentLanguage] || dealer.label?.en || '',
      // 1.0's dealership <option value> is always the English label, so the
      // payload reads the same regardless of the UI locale.
      labelEn: dealer.label?.en || dealer.dealerName || '',
      dealerId: dealer.dealerId,
    }));
  }, [country, currentLanguage]);

  const modelName = uimetadata.config?.modelName || 'Traverse';
  const brandName = uimetadata.config?.brand || 'chevrolet';
  const capitalizedBrand =
    brandName.charAt(0).toUpperCase() + brandName.slice(1);
  const capitalizedModel =
    modelName.charAt(0).toUpperCase() + modelName.slice(1);
  const models = [`${capitalizedBrand} ${capitalizedModel}`];

  const validationFields = useMemo(() => {
    const fields: any = {};
    if (!selectedOption) return fields;
    if (!uimetadata.saveFormConfig?.fields) {
      return {};
    }

    uimetadata.saveFormConfig?.fields
      .filter(
        (f) => !f.visibility || f.visibility.includes(selectedOption as string)
      )
      .forEach((f) => {
        // `requiredFor`, when present, narrows `required` to a subset of the
        // options the field is visible on (1.0's save form shows the dealership
        // dropdown but does not gate submission on it).
        const requiredFor = (f as { requiredFor?: string[] }).requiredFor;
        const required = requiredFor
          ? f.required && requiredFor.includes(selectedOption as string)
          : f.required;
        fields[f.id] = {
          value: formData[f.id] || '',
          type:
            f.type === 'textarea'
              ? 'text'
              : f.type === 'select'
              ? 'text'
              : f.type,
          required,
          label: t(`staticTranslations.${f.labelKey}`, f.id),
        };
      });
    return fields;
  }, [formData, selectedOption, t]);

  const {
    errors,
    isValid: isFormValid,
    validate,
    validateSingleField,
    clearError,
    clearAllErrors,
  } = useFormValidation(validationFields);

  useEffect(() => {
    if (isOpen) {
      trackEvent(AnalyticsIds.proceedModalView, {
        parent: source,
        primaryTypeName: 'lead',
      });
      setStep('options');
      setSelectedOption(null);
      setIsSent(false);
      setIsFailed(false);
      setBackendError(null);
      clearAllErrors();
      if (qrCodeUrlRef.current) {
        URL.revokeObjectURL(qrCodeUrlRef.current);
        qrCodeUrlRef.current = '';
      }
      setQrCodeUrl('');
      setSelectedPrefix(dialCode);
      const modelName = uimetadata.config?.modelName || 'Traverse';
      const brandName = uimetadata.config?.brand || 'chevrolet';
      const capitalizedBrand =
        brandName.charAt(0).toUpperCase() + brandName.slice(1);
      const capitalizedModel =
        modelName.charAt(0).toUpperCase() + modelName.slice(1);
      const fullModelName = `${capitalizedBrand} ${capitalizedModel}`;
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        model: fullModelName,
        dealer: '',
        testDriveDate: '',
        testDriveTime: '',
        comments: '',
      });
      setIsMarketingAccepted(true);
    }
  }, [isOpen, clearAllErrors, dialCode, source, trackEvent]);

  const LEAD_NAME_BY_OPTION: Record<SaveOption, string> = {
    'test-drive': 'testDrive',
    callback: 'callBack',
    whatsapp: 'saveToWhatsapp',
    email: 'saveToEmail',
  };

  const handleOptionClick = (option: SaveOption) => {
    setSelectedOption(option);
    setStep('form');
    setBackendError(null);
    const { state_info } = resolveConfigurationContext();
    trackEvent(AnalyticsIds.bookingCtaInteract, {
      parent: source,
      primaryTypeName: state_info.exterior_color,
      secondaryTypeName: LEAD_NAME_BY_OPTION[option],
    });
    const formLabel =
      option === 'whatsapp'
        ? t('staticTranslations.saveDialog.optionWhatsapp', 'Save to WhatsApp')
        : option === 'email'
        ? t('staticTranslations.saveDialog.optionEmail', 'Save to Email')
        : option === 'test-drive'
        ? t(
            'staticTranslations.saveDialog.optionTestDrive',
            'Book a Test Drive'
          )
        : t(
            'staticTranslations.saveDialog.optionCallback',
            'Request a call Back'
          );
    updateGMDigitalDataSiteSection(`${formLabel} form`);
  };

  const handleBack = useCallback(() => {
    setStep('options');
    setSelectedOption(null);
    setIsSent(false);
    setIsFailed(false);
    setBackendError(null);
    clearAllErrors();
  }, [clearAllErrors]);

  const handleDialogClose = useCallback(() => {
    if (!isSent) {
      const fieldsCompleted = Object.values(formData).filter(Boolean).length;
      trackEvent(AnalyticsIds.leadFormClose, {
        parent: 'leads',
        primaryTypeName:
          step === 'options'
            ? 'proceedModal'
            : selectedOption
            ? LEAD_NAME_BY_OPTION[selectedOption]
            : '',
        secondaryTypeName: String(fieldsCompleted),
      });
    }
    handleBack();
    onClose();
  }, [isSent, formData, step, selectedOption, trackEvent, handleBack, onClose]);

  /**
   * Model / variant / colour / accessory labels and the shareable deep link,
   * resolved once per submission. Mirrors 1.0's `useShareURL`
   * (`getShareParams` + `createURLToShare`).
   */
  const resolveConfigurationContext = useCallback(() => {
    const activeModelKey = uimetadata.config?.model || '';
    const modelMeta = (uimetadata as any)?.models?.[activeModelKey];
    const variantData = modelMeta?.variants?.[variantID];

    const extColorId = exteriorColorID;
    const intColorId = interiorColorID;
    const backgroundId = backgroundIDs?.[0];

    const extColorData = modelMeta?.colors?.exterior?.[extColorId || ''];
    const intColorData = modelMeta?.colors?.interior?.[intColorId || ''];
    const backgroundData = modelMeta?.backgrounds?.[backgroundId || ''];

    const label = (obj: any, fallback = '') =>
      obj?.[currentLanguage] || obj?.en || fallback;

    const variantName = label(variantData?.name, variantID || '');
    // Salesforce validates state_info.variant as "YYYY <variant>" (Mule maps it
    // onto a year-checked field): the variant's API-only leadModelYear, then its
    // English name -- English so the year always leads, with any year the name
    // already starts with dropped so it isn't doubled.
    const variantNameEn = variantData?.name?.en || variantID || '';
    const leadVariant = [
      (variantData as any)?.leadModelYear,
      variantNameEn.replace(/^\d{4}\s+/, ''),
    ]
      .filter(Boolean)
      .join(' ');
    const extColorName = label(extColorData?.label, extColorId || '');
    const intColorName = label(intColorData?.label, intColorId || '');
    const backgroundName = label(
      backgroundData?.title || backgroundData?.label,
      backgroundId || ''
    );

    // Accessory + package labels, comma-joined, in metadata order — 1.0's
    // state_info.accessories is the same comma-joined list of labels.
    const selectedIds = [...selectedAccessoryIDs, ...selectedPackageIDs];
    const accessoryNames = [
      ...Object.keys(modelMeta?.accessories || {}),
      ...Object.keys(modelMeta?.packages || {}),
    ]
      .filter((id) => selectedIds.includes(id))
      .map((id) =>
        label(
          modelMeta?.accessories?.[id]?.label || modelMeta?.packages?.[id]?.label
        )
      )
      .filter(Boolean)
      .join(',');

    // 1.0 screenshot path: /Screenshots/<Variant Name Description>/<Colour>.jpg,
    // always in English regardless of the active locale.
    const variantFolder = variantData?.description?.en
      ? `${variantData?.name?.en} ${variantData?.description?.en}`
      : variantData?.name?.en || '';
    const screenshot = `https://autodome.metadome.ai/resources/${linkId}/Screenshots/${encodeURIComponent(
      variantFolder
    )}/${encodeURIComponent(extColorData?.label?.en || '')}.jpg`;

    const cart = {
      colorAddedToCart: false,
      accessoriesListToCart: [] as Array<{ name: string; quantity: number }>,
    };

    const accessoriesParam = encodeURIComponent(
      selectedAccessoryIDs.join(';') + (selectedAccessoryIDs.length ? ';' : '')
    );
    const cartParam = encodeURIComponent(btoa(JSON.stringify(cart)));
    const modelConfId =
      uimetadata.config?.configurationId || activeModelKey.toLowerCase();
    const variantConfId = (variantData as any)?.configurationId || variantID;
    const shareParams = [
      `model=${encodeURIComponent(modelConfId || '')}`,
      `variant=${encodeURIComponent(variantConfId || '')}`,
      `exterior_color=${encodeURIComponent(extColorId || '')}`,
      `interior_color=${encodeURIComponent(intColorId || '')}`,
      `accessories=${accessoriesParam}`,
      `background=${encodeURIComponent(backgroundId || '')}`,
      `cart=${cartParam}`,
    ].join('&');
    const link = `https://autodome.metadome.ai/${linkId}/${getApiMode()}/index.html#/presentation?${shareParams}`;

    // Lead-side model name: 1.0's `config.model` (a lowercase lead label,
    // distinct from 2.0's `config.model` which is the models[] key), ported
    // onto `config.leadModel`.
    const leadModel =
      (uimetadata.config as any)?.leadModel || uimetadata.config?.model || '';

    const state_info = {
      model: leadModel,
      variant: leadVariant,
      exterior_color: extColorName,
      interior_color: intColorName,
      accessories: accessoryNames,
      background: backgroundName,
      language: currentLanguage,
      brand: uimetadata.config?.brand || '',
      screenshots: [screenshot],
      cart,
      // 2.0-only: the active view and feature, carried over from the previous
      // implementation so nothing that already consumed them regresses.
      view: view?.type,
      features: featureId ? [featureId] : [],
    };

    const configuredVehicleCode = getConfiguredVehicleCode({
      variantID,
      exteriorColorID: extColorId,
      interiorColorID: intColorId,
      accessoryIDs: selectedAccessoryIDs,
      packageIDs: selectedPackageIDs,
    });

    return { state_info, link, configuredVehicleCode, leadModel, variantName };
  }, [
    backgroundIDs,
    currentLanguage,
    exteriorColorID,
    featureId,
    interiorColorID,
    selectedAccessoryIDs,
    selectedPackageIDs,
    variantID,
    view,
  ]);

  const handleSendConfig = async () => {
    if (!validate()) {
      // `errors` state from useFormValidation hasn't re-rendered yet at this
      // point, so we can't reliably list the invalid fields here.
      trackEvent(AnalyticsIds.leadFormError, {
        parent: selectedOption ? LEAD_NAME_BY_OPTION[selectedOption] : '',
        primaryTypeName: 'validation',
      });
      return;
    }

    setLoading(true);
    setIsFailed(false);
    setBackendError(null);

    const baseURL = ECOMM_SVC[getApiMode()];

    try {
      const isBooking =
        selectedOption === 'test-drive' || selectedOption === 'callback';
      const isTestDrive = selectedOption === TEST_DRIVE_OPTION;

      const client_config = uimetadata.config?.client || 'GM';
      const brand_config = uimetadata.config?.brand || 'chevrolet';

      const { state_info, link, configuredVehicleCode, leadModel } =
        resolveConfigurationContext();

      // Salesforce source-code segments. `at_showroom` / `pre_showroom` for the
      // trigger_info flow name, `atshowroom` / `preshowroom` for the short
      // lead_details source code.
      const inShowroom = getShowroom() === 'in';
      const showroomSegment = inShowroom ? 'at_showroom' : 'pre_showroom';
      const sourceCodeSegment = inShowroom ? 'atshowroom' : 'preshowroom';

      const trigger = {
        'test-drive': {
          channel: 'email',
          source: `Metadome_configurator_${showroomSegment}_Book_a_Test_Drive`,
          campaign_type: 'TD',
          leadSource: `md_${sourceCodeSegment}`,
        },
        callback: {
          channel: 'email',
          source: `Metadome_configurator_${showroomSegment}_Request_a_Quote`,
          campaign_type: 'RESERVE',
          leadSource: `md_${sourceCodeSegment}`,
        },
        whatsapp: {
          channel: 'whatsapp',
          source: `Metadome_configurator_${showroomSegment}_Save_To_WhatsApp`,
          campaign_type: 'WHATSAPP',
          leadSource: `md_${sourceCodeSegment}_save_wa`,
        },
        email: {
          channel: 'email',
          source: `Metadome_configurator_${showroomSegment}_Save_To_Email`,
          campaign_type: 'EMAIL',
          leadSource: `md_${sourceCodeSegment}_save_email`,
        },
      }[selectedOption as Exclude<SaveOption, null>];

      const selectedDealerData = dealers.find(
        (d: any) => d.address === formData.dealer || d.name === formData.dealer
      );

      // The test drive slot has no dedicated field on the CRM side, so it rides
      // along in comments: appended after whatever the customer typed, or on
      // its own if the comments box was left blank.
      const testDriveComment =
        isTestDrive && formData.testDriveDate && formData.testDriveTime
          ? // <input type="time"> only ever yields HH:MM; append seconds to
            // match the HH:MM:SS convention the SFTP lead file already uses.
            `${formatDateForLead(formData.testDriveDate)} ${
              formData.testDriveTime
            }:00`
          : '';
      const comments = [formData.comments, testDriveComment]
        .filter(Boolean)
        .join(' ');

      // ---- user/v1 body (1.0's IBookingDetails) -----------------------------
      const bookingDetails: Record<string, unknown> = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        // 1.0 sends the bare number from the booking forms and the
        // prefixed number from the save forms; kept as-is.
        mobile_number: isBooking
          ? formData.phone
          : selectedPrefix + formData.phone,
        address: '',
        city: '',
        country: '',
        preferences: {
          dealership: selectedDealerData?.labelEn || formData.dealer,
          dealership_id: selectedDealerData?.dealerId || '',
          // Just the dealer, without the location/label suffix `dealership`
          // (the dropdown label) carries. Feeds the lead file's Dealer column.
          dealer_name: selectedDealerData?.dealerName || '',
        },
        lead_details: {
          source: trigger.leadSource,
          channel: isBooking
            ? searchParams.get('channel') ?? ''
            : trigger.channel,
          // 1.0's booking forms send the model dropdown's `value` (e.g.
          // 'Optiq'), not the label it displays ('CADILLAC OPTIQ'); its save
          // forms send the lead model name instead.
          model_selection: isBooking
            ? uimetadata.config?.modelName || formData.model
            : leadModel,
          purchase_in: '',
          comments: isBooking ? comments : '',
        },
        client: client_config,
      };
      // 1.0's save forms carry the configured-vehicle code on the user record
      // too; its booking forms do not.
      if (!isBooking) bookingDetails.configuredVehicleCode = configuredVehicleCode;

      // ---- configuration/v2 body (1.0's IConfiguration) ---------------------
      const configuration: Record<string, unknown> = {
        fname: formData.firstName,
        lname: formData.lastName,
        // Save-to-WhatsApp collects no email, Save-to-Email collects no phone.
        email: selectedOption === 'whatsapp' ? '' : formData.email,
        mobile_number: selectedPrefix + formData.phone,
        client: client_config,
        brand: brand_config,
        // The variant's lead-file catalogue code — the same code used as the
        // first segment of configuredVehicleCode — rather than the model-level
        // modelId. Not the AutoIntel id: that is a different namespace.
        modelId:
          (variantID ? getBrandItemCode(variantID) : '') ||
          uimetadata.config?.modelId ||
          '',
        modelName: leadModel,
        link,
        configuredVehicleCode,
        metadome_country_code: leadCountryCode,
        state_info,
        // <input type="date"> yields YYYY-MM-DD; the WhatsApp template renders
        // DD/MM/YYYY. Time is already HH:MM from <input type="time">.
        test_drive_date: isTestDrive
          ? formatDateForLead(formData.testDriveDate)
          : '',
        test_drive_time: isTestDrive ? formData.testDriveTime : '',
        showroom: getShowroom(),
        lead_model_name: uimetadata.config?.leadModelName,
        // Marketing consent: checked = opted in ('no'), unchecked = opt out ('yes').
        optout: isMarketingAccepted ? 'no' : 'yes',
        trigger_info: [
          {
            channel: trigger.channel,
            source: trigger.source,
            campaign_type: trigger.campaign_type,
            privacy: !isMarketingAccepted,
          },
        ],
        userId: '',
      };

      // ---- 1.0's booking(): user/v1, then configuration/v2 for GM ----------
      const userRes = await fetch(`${baseURL}/user/v1`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bookingDetails),
      });

      if (!userRes.ok) throw new Error('Failed to create user');

      const userJson = await userRes.json().catch(() => ({} as any));
      const createdUserId = (userJson as any)?.data?.id ?? '';

      if (!createdUserId) {
        setBackendError('User creation failed: missing user id');
        setLoading(false);
        return;
      }
      configuration.user_id = createdUserId;

      // GM (Salesforce integration) uses the v2 endpoint, which answers with
      // the configuration QR code as a PNG blob. Non-GM clients stop here —
      // 1.0 removed the legacy configuration/v1 path entirely.
      if (client_config === 'GM') {
        const configRes = await fetch(`${baseURL}/configuration/v2`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(configuration),
        });

        if (!configRes.ok) {
          let errorMessage = 'Failed to send lead';
          try {
            const errorData = await configRes.json();
            if (errorData && errorData.message) errorMessage = errorData.message;
          } catch (e) {
            /* non-JSON error body */
          }
          throw new Error(errorMessage);
        }

        // The QR is the response body itself, but only the booking forms
        // surface it — 1.0's save forms (SaveNShare) call the same endpoint and
        // discard `qrCodeResponse`, so Save to WhatsApp / Save to Email show no
        // QR box. Reading the body is skipped entirely for those.
        if (isBooking) {
          // Accept the body only when it really is an image: an error path can
          // answer 200 with a JSON ack, and piping that into an <img> renders a
          // broken-image box rather than nothing, which reads as "the QR is
          // missing". Log the content type so a failing flow is diagnosable.
          const blob = await configRes.blob();
          const isImage =
            blob.size > 0 && (blob.type.startsWith('image/') || blob.type === '');
          if (isImage) {
            setQrFromBlob(blob);
          } else {
            console.warn(
              `[SaveDialog] configuration/v2 returned no QR image for "${selectedOption}" ` +
                `(content-type: ${blob.type || 'none'}, ${blob.size} bytes)`
            );
          }
        }
      }

      setIsSent(true);
      trackEvent(AnalyticsIds.leadFormSubmit, {
        parent: source,
        primaryTypeName: state_info.exterior_color,
        secondaryTypeName: selectedOption ? LEAD_NAME_BY_OPTION[selectedOption] : '',
        tertiaryTypeName: formData.dealer,
        // Field names only — never values — per the Guide tab's PII rule.
        formData: Object.keys(formData)
          .filter((key) => (formData as Record<string, string>)[key])
          .join(','),
      });
      if (selectedOption === 'whatsapp' || selectedOption === 'email') {
        updateGMDigitalDataSiteSection(
          `Save to ${selectedOption === 'whatsapp' ? 'WhatsApp' : 'Email'}`
        );
      }
      updateGMDigitalDataSiteSection(DataDtm.LeadForms);
      trackAutoIntelOnly(
        isBooking
          ? isTestDrive
            ? 'CONVERSION_TEST_DRIVE'
            : 'CONVERSION_PHONE'
          : 'CONVERSION_SAVE',
        variantID || 'unknown',
        selectedOption + ' Success',
        isBooking
          ? isTestDrive
            ? 'Test Drive Request'
            : 'Callback Request'
          : 'Save Configuration'
      );
    } catch (error) {
      console.error('Error sending lead:', error);
      trackEvent(AnalyticsIds.leadFormError, {
        parent: selectedOption ? LEAD_NAME_BY_OPTION[selectedOption] : '',
        primaryTypeName: 'api',
        tertiaryTypeName: error instanceof Error ? error.message : String(error),
      });
      setBackendError(
        error instanceof Error
          ? error.message
          : 'Request failed, please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const activeModelKey = uimetadata.config?.model || '';
  const variantData = (uimetadata as any)?.models?.[activeModelKey]?.variants?.[
    variantID
  ];
  const variantName = [
    variantData?.name?.[currentLanguage] || variantData?.name?.en,
    variantData?.description?.[currentLanguage] || variantData?.description?.en,
  ]
    .filter(Boolean)
    .join(' ');

  return {
    step,
    selectedOption,
    selectedPrefix,
    setSelectedPrefix,
    isMarketingAccepted,
    setIsMarketingAccepted,
    backendError,
    setBackendError,
    firstName: formData.firstName,
    setFirstName: (val: string) => setFieldValue('firstName', val),
    lastName: formData.lastName,
    setLastName: (val: string) => setFieldValue('lastName', val),
    email: formData.email,
    setEmail: (val: string) => setFieldValue('email', val),
    phone: formData.phone,
    setPhone: (val: string) => setFieldValue('phone', val),
    selectedModel: formData.model,
    setSelectedModel: (val: string) => setFieldValue('model', val),
    selectedDealer: formData.dealer,
    setSelectedDealer: (val: string) => setFieldValue('dealer', val),
    testDriveDate: formData.testDriveDate,
    testDriveTime: formData.testDriveTime,
    comments: formData.comments,
    setComments: (val: string) => setFieldValue('comments', val),
    setFieldValue,
    loading,
    isSent,
    isFailed,
    variantID,
    variantName,
    dealers,
    models,
    errors,
    isFormValid: selectedOption ? isFormValid : false,
    qrCodeUrl,
    downloadQRCode,
    openQRCodeInNewTab,
    handleOptionClick,
    handleBack,
    handleDialogClose,
    handleSendConfig,
    validateSingleField,
    clearError,
    t,
  };
};
